import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, User, Building2, Plus, X, Briefcase, Mail, Phone, MapPin, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { auth } from '../utils/auth';
import { clientsApi } from '../services';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreateClient = () => {
  const navigate = useNavigate();
  const user = auth.getUser();
  
  const [clientType, setClientType] = useState('PERSON');
  const [loading, setLoading] = useState(false);
  
  // Brreg search
  const [brregSearch, setBrregSearch] = useState('');
  const [brregResults, setBrregResults] = useState([]);
  const [brregLoading, setBrregLoading] = useState(false);
  const [showBrregResults, setShowBrregResults] = useState(false);
  
  // Person fields
  const [personData, setPersonData] = useState({
    person_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    notes: ''
  });
  
  // Company fields
  const [companyData, setCompanyData] = useState({
    company_name: '',
    org_nr: '',
    industry: '',
    website: '',
    address: '',
    city: '',
    postal_code: '',
    notes: ''
  });
  
  // Contacts for company
  const [contacts, setContacts] = useState([
    {
      id: Date.now().toString(),
      name: '',
      email: '',
      phone: '',
      position: '',
      role: 'PRIMARY',
      is_internal: true,
      notes: ''
    }
  ]);
  
  const addContact = () => {
    setContacts([
      ...contacts,
      {
        id: Date.now().toString(),
        name: '',
        email: '',
        phone: '',
        position: '',
        role: 'SECONDARY',
        is_internal: true,
        notes: ''
      }
    ]);
  };
  
  const removeContact = (id) => {
    if (contacts.length === 1) {
      toast.error('Må ha minst én kontaktperson');
      return;
    }
    setContacts(contacts.filter(c => c.id !== id));
  };
  
  const updateContact = (id, field, value) => {
    setContacts(contacts.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    ));
  };
  
  // Brreg search function
  const searchBrreg = async (query) => {
    if (query.length < 2) {
      setBrregResults([]);
      setShowBrregResults(false);
      return;
    }
    
    setBrregLoading(true);
    try {
      const response = await axios.get(`${API}/brreg/search`, {
        params: { q: query, size: 10 }
      });
      
      if (response.data.success) {
        setBrregResults(response.data.companies);
        setShowBrregResults(true);
      }
    } catch (error) {
      console.error('Brreg search error:', error);
      toast.error('Kunne ikke søke i Brønnøysundregistrene');
    } finally {
      setBrregLoading(false);
    }
  };
  
  // Auto-fill company data from Brreg
  const selectBrregCompany = async (orgnr) => {
    try {
      const response = await axios.get(`${API}/brreg/enhet/${orgnr}`);
      const company = response.data;
      
      // Parse address
      const address = company.forretningsadresse?.adresse?.[0] || '';
      const postalCode = company.forretningsadresse?.postnummer || '';
      const city = company.forretningsadresse?.poststed || '';
      
      // Update company data
      setCompanyData({
        ...companyData,
        company_name: company.navn || '',
        org_nr: company.organisasjonsnummer || '',
        address: address,
        postal_code: postalCode,
        city: city,
        website: company.hjemmeside || '',
        industry: company.naeringskode1?.beskrivelse || ''
      });
      
      setShowBrregResults(false);
      setBrregSearch('');
      toast.success(`Hentet data for ${company.navn}`);
    } catch (error) {
      console.error('Error fetching company details:', error);
      toast.error('Kunne ikke hente firmaopplysninger');
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = auth.getToken();
      
      // Helper to convert empty strings to null
      const cleanData = (data) => {
        const cleaned = {};
        for (const [key, value] of Object.entries(data)) {
          cleaned[key] = value === '' ? null : value;
        }
        return cleaned;
      };
      
      // Clean contacts data
      const cleanedContacts = clientType === 'COMPANY' 
        ? contacts
            .filter(c => c.name.trim())
            .map(c => ({
              ...c,
              email: c.email?.trim() || null,
              phone: c.phone?.trim() || null,
              position: c.position?.trim() || null,
              notes: c.notes?.trim() || null
            }))
        : [];
      
      const clientPayload = {
        lawyer_id: user.id,
        type: clientType,
        ...(clientType === 'PERSON' ? cleanData(personData) : cleanData(companyData)),
        contacts: cleanedContacts,
        status: 'ACTIVE',
        tags: []
      };
      
      await axios.post(`${API}/clients`, clientPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(
        clientType === 'PERSON' 
          ? 'Klient opprettet!' 
          : 'Bedriftsklient opprettet!'
      );
      
      navigate('/lexy/dashboard?tab=clients');
    } catch (error) {
      console.error('Error creating client:', error);
      
      // Handle validation errors from FastAPI
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array of validation errors
        if (Array.isArray(detail)) {
          const errorMessages = detail.map(err => {
            const field = err.loc?.join('.') || 'field';
            return `${field}: ${err.msg}`;
          }).join(', ');
          toast.error(`Valideringsfeil: ${errorMessages}`);
        } else if (typeof detail === 'string') {
          toast.error(detail);
        } else {
          toast.error('Kunne ikke opprette klient');
        }
      } else {
        toast.error('Kunne ikke opprette klient');
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-[#F4F2EE]">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Button
            onClick={() => navigate('/lexy/dashboard?tab=clients')}
            variant="outline"
            size="sm"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Tilbake til klienter
          </Button>
          
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Opprett klient</h1>
          <p className="text-slate-600">Registrer en ny privatkunde eller bedrift</p>
        </div>
      </div>
      
      {/* Form */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Type Selector */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <label className="block text-sm font-bold text-slate-900 mb-3">Klienttype</label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setClientType('PERSON')}
                className={`p-6 rounded-xl border-2 transition-all ${
                  clientType === 'PERSON'
                    ? 'border-slate-600 bg-slate-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <User className={`w-8 h-8 mx-auto mb-3 ${
                  clientType === 'PERSON' ? 'text-slate-900' : 'text-slate-400'
                }`} />
                <div className="font-bold text-slate-900">Privatperson</div>
                <div className="text-sm text-slate-600">Enkeltperson</div>
              </button>
              
              <button
                type="button"
                onClick={() => setClientType('COMPANY')}
                className={`p-6 rounded-xl border-2 transition-all ${
                  clientType === 'COMPANY'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <Building2 className={`w-8 h-8 mx-auto mb-3 ${
                  clientType === 'COMPANY' ? 'text-blue-600' : 'text-slate-400'
                }`} />
                <div className="font-bold text-slate-900">Bedrift</div>
                <div className="text-sm text-slate-600">Selskap/organisasjon</div>
              </button>
            </div>
          </div>
          
          {/* Person Form */}
          {clientType === 'PERSON' && (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Personinformasjon</h3>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Fullt navn *
                </label>
                <Input
                  value={personData.person_name}
                  onChange={(e) => setPersonData({...personData, person_name: e.target.value})}
                  placeholder="Ola Nordmann"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    E-post
                  </label>
                  <Input
                    type="email"
                    value={personData.email}
                    onChange={(e) => setPersonData({...personData, email: e.target.value})}
                    placeholder="ola@example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Telefon
                  </label>
                  <Input
                    value={personData.phone}
                    onChange={(e) => setPersonData({...personData, phone: e.target.value})}
                    placeholder="+47 123 45 678"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Adresse
                </label>
                <Input
                  value={personData.address}
                  onChange={(e) => setPersonData({...personData, address: e.target.value})}
                  placeholder="Gateadresse"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Postnummer
                  </label>
                  <Input
                    value={personData.postal_code}
                    onChange={(e) => setPersonData({...personData, postal_code: e.target.value})}
                    placeholder="0123"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    By
                  </label>
                  <Input
                    value={personData.city}
                    onChange={(e) => setPersonData({...personData, city: e.target.value})}
                    placeholder="Oslo"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Notater
                </label>
                <textarea
                  value={personData.notes}
                  onChange={(e) => setPersonData({...personData, notes: e.target.value})}
                  placeholder="Interne notater om klienten..."
                  className="w-full p-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-600"
                  rows={3}
                />
              </div>
            </div>
          )}
          
          {/* Company Form */}
          {clientType === 'COMPANY' && (
            <>
              {/* Brreg Search */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-bold text-slate-900">Søk i Brønnøysundregistrene</h3>
                </div>
                <p className="text-sm text-slate-600 mb-4">
                  Finn og hent bedriftsinformasjon automatisk fra offentlige registre
                </p>
                
                <div className="relative">
                  <Input
                    value={brregSearch}
                    onChange={(e) => {
                      setBrregSearch(e.target.value);
                      searchBrreg(e.target.value);
                    }}
                    placeholder="Søk etter bedriftsnavn eller org.nr..."
                    className="bg-white"
                  />
                  {brregLoading && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
                
                {/* Search Results Dropdown */}
                {showBrregResults && brregResults.length > 0 && (
                  <div className="mt-2 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {brregResults.map((company) => (
                      <button
                        key={company.organisasjonsnummer}
                        type="button"
                        onClick={() => selectBrregCompany(company.organisasjonsnummer)}
                        className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 transition-colors"
                      >
                        <div className="font-medium text-slate-900">{company.navn}</div>
                        <div className="text-sm text-slate-600 mt-1">
                          Org.nr: {company.organisasjonsnummer}
                          {company.forretningsadresse?.formatted && (
                            <span className="ml-2">• {company.forretningsadresse.formatted}</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                
                {showBrregResults && brregResults.length === 0 && !brregLoading && brregSearch.length >= 2 && (
                  <div className="mt-2 bg-white border border-slate-200 rounded-lg p-4 text-center text-sm text-slate-600">
                    Ingen bedrifter funnet
                  </div>
                )}
              </div>
              
              {/* Company Info */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Bedriftsinformasjon</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Bedriftsnavn *
                    </label>
                    <Input
                      value={companyData.company_name}
                      onChange={(e) => setCompanyData({...companyData, company_name: e.target.value})}
                      placeholder="Acme AS"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Org.nr
                    </label>
                    <Input
                      value={companyData.org_nr}
                      onChange={(e) => setCompanyData({...companyData, org_nr: e.target.value})}
                      placeholder="123456789"
                      maxLength={9}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Bransje
                    </label>
                    <Input
                      value={companyData.industry}
                      onChange={(e) => setCompanyData({...companyData, industry: e.target.value})}
                      placeholder="IT, Bygg, Handel, etc."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Nettside
                    </label>
                    <Input
                      value={companyData.website}
                      onChange={(e) => setCompanyData({...companyData, website: e.target.value})}
                      placeholder="https://acme.no"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Adresse
                  </label>
                  <Input
                    value={companyData.address}
                    onChange={(e) => setCompanyData({...companyData, address: e.target.value})}
                    placeholder="Gateadresse"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Postnummer
                    </label>
                    <Input
                      value={companyData.postal_code}
                      onChange={(e) => setCompanyData({...companyData, postal_code: e.target.value})}
                      placeholder="0123"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      By
                    </label>
                    <Input
                      value={companyData.city}
                      onChange={(e) => setCompanyData({...companyData, city: e.target.value})}
                      placeholder="Oslo"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Notater
                  </label>
                  <textarea
                    value={companyData.notes}
                    onChange={(e) => setCompanyData({...companyData, notes: e.target.value})}
                    placeholder="Interne notater om bedriften..."
                    className="w-full p-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
              </div>
              
              {/* Contacts */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-slate-900">Kontaktpersoner</h3>
                  <Button
                    type="button"
                    onClick={addContact}
                    size="sm"
                    variant="outline"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Legg til kontakt
                  </Button>
                </div>
                
                <div className="space-y-4">
                  {contacts.map((contact, index) => (
                    <div key={contact.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-bold text-slate-700">
                          Kontakt {index + 1}
                          {contact.role === 'PRIMARY' && (
                            <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                              Hovedkontakt
                            </span>
                          )}
                        </span>
                        {contacts.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeContact(contact.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          value={contact.name}
                          onChange={(e) => updateContact(contact.id, 'name', e.target.value)}
                          placeholder="Navn *"
                          required={index === 0}
                        />
                        
                        <Input
                          value={contact.position}
                          onChange={(e) => updateContact(contact.id, 'position', e.target.value)}
                          placeholder="Stilling (f.eks. CEO)"
                        />
                        
                        <Input
                          type="email"
                          value={contact.email}
                          onChange={(e) => updateContact(contact.id, 'email', e.target.value)}
                          placeholder="E-post"
                        />
                        
                        <Input
                          value={contact.phone}
                          onChange={(e) => updateContact(contact.id, 'phone', e.target.value)}
                          placeholder="Telefon"
                        />
                      </div>
                      
                      <div className="mt-3 flex gap-4">
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={`contact_role_${contact.id}`}
                            checked={contact.role === 'PRIMARY'}
                            onChange={() => updateContact(contact.id, 'role', 'PRIMARY')}
                            className="w-4 h-4"
                          />
                          <span className="text-sm text-slate-700">Hovedkontakt</span>
                        </label>
                        
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={`contact_role_${contact.id}`}
                            checked={contact.role === 'SECONDARY'}
                            onChange={() => updateContact(contact.id, 'role', 'SECONDARY')}
                            className="w-4 h-4"
                          />
                          <span className="text-sm text-slate-700">Sekundær</span>
                        </label>
                        
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={contact.is_internal}
                            onChange={(e) => updateContact(contact.id, 'is_internal', e.target.checked)}
                            className="w-4 h-4"
                          />
                          <span className="text-sm text-slate-700">Intern (ansatt)</span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
          
          {/* Actions */}
          <div className="flex gap-3">
            <Button
              type="button"
              onClick={() => navigate('/lexy/dashboard?tab=clients')}
              variant="outline"
              className="flex-1"
            >
              Avbryt
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className={`flex-1 ${
                clientType === 'PERSON' 
                  ? 'bg-slate-900 hover:bg-slate-800' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? (
                <>Oppretter...</>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Opprett {clientType === 'PERSON' ? 'klient' : 'bedrift'}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateClient;
